import { Conversation } from 'src/models';

function buildPrintContent(conversation: Partial<Conversation>, timelineEvents: any[]): string {
  // Build header
  let content = `
    <div class="header">
      <h1 class="conversation-title">${conversation?.title || 'Untitled Conversation'}</h1>
      <p class="conversation-meta">
        Conversation #${conversation?.friendlyId || 'Unknown'} | 
        Created: ${conversation?.createdAt ? new Date(conversation.createdAt).toLocaleString() : 'Unknown'} |
        Status: ${conversation?.status || 'Unknown'}
        ${conversation?.assignedToUser ? ` | Assigned to: ${conversation.assignedToUser.name}` : ''}
      </p>
    </div>
  `;

  for (const event of timelineEvents) {
    if (event.type === 'message') {
      const message = event.data;

      content += `
        <div class="message">
          <div class="message-header">
            <span class="message-author">${message.user?.name || message.sentByUser?.name || 'Unknown User'}</span>
            <span class="message-time">${new Date(message.timestamp).toLocaleString()}</span>
            ${message.isPrivate ? '<span style="color: #856404; font-style: italic;">(Internal Note)</span>' : ''}
          </div>
          <div class="message-content ${message.isPrivate ? 'message-content-private' : ''}">
            ${message.resolvedContent || message.text || 'No content'}
          </div>
      `;

      // Add file attachments
      if (message.files?.length) {
        content += '<div style="margin-top: 10px;">';
        for (const file of message.files) {
          content += `
            <div class="file-attachment">
              📎 ${file.name || 'Attachment'}
            </div>
          `;
        }
        content += '</div>';
      }

      // Add reactions
      if (message.reactions?.length) {
        content += '<div class="reactions">';
        for (const reaction of message.reactions) {
          content += `
            <span class="reaction">
              ${reaction.name}
            </span>
          `;
        }
        content += '</div>';
      }

      content += '</div>';
    } else if (event.type === 'auditItem') {
      const audit = event.data;
      content += `
        <div class="audit-item">
          <strong>${audit.type}</strong> - 
          ${new Date(audit.timestamp).toLocaleString()}
          ${audit.data?.description ? `<br>${audit.data.description}` : ''}
        </div>
      `;
    } else if (event.type === 'conversationLink') {
      const link = event.data;
      content += `
        <div class="conversation-link">
          <strong>${link.label}</strong> - 
          ${new Date(event.timestamp).toLocaleString()}
          <br>Linked to conversation #${link.linkedConversationId}
        </div>
      `;
    }
  }

  // Add footer
  content += `
    <div class="footer">
      <p>Printed from Unthread on ${new Date().toLocaleString()}</p>
      <p>Conversation ID: ${conversation?.id || 'Unknown'}</p>
    </div>
  `;

  return content;
}

export function printConversation(conversation: Partial<Conversation>, timelineEvents: any[]) {
  // Create a print-friendly version of the conversation
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    return;
  }

  // Build the print content
  const printContent = buildPrintContent(conversation, timelineEvents);

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Conversation #${conversation?.friendlyId || 'Unknown'}</title>
        <style>
          @media print {
            body { margin: 0; padding: 20px; }
            .page-break { page-break-before: always; }
            .no-break { page-break-inside: avoid; }
          }
          
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
          }
          
          .header {
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
            margin-bottom: 30px;
          }
          
          .conversation-title {
            font-size: 24px;
            font-weight: bold;
            margin: 0 0 10px 0;
          }
          
          .conversation-meta {
            font-size: 14px;
            color: #666;
            margin: 0;
          }
          
          .message {
            margin-bottom: 25px;
            page-break-inside: avoid;
          }
          
          .message-header {
            display: flex;
            align-items: center;
            margin-bottom: 8px;
            font-size: 14px;
          }
          
          .message-author {
            font-weight: bold;
            margin-right: 10px;
          }
          
          .message-time {
            color: #666;
          }
          
          .message-content {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 8px;
            padding: 15px;
            margin-left: 0;
          }
          
          .message-content-private {
            background: #fff3cd;
            border-color: #ffeaa7;
          }
          
          .audit-item {
            background: #e3f2fd;
            border: 1px solid #bbdefb;
            border-radius: 8px;
            padding: 10px;
            margin-bottom: 15px;
            font-size: 14px;
          }
          
          .audit-item strong {
            color: #1976d2;
          }
          
          .conversation-link {
            background: #f3e5f5;
            border: 1px solid #e1bee7;
            border-radius: 8px;
            padding: 10px;
            margin-bottom: 15px;
            font-size: 14px;
          }
          
          .conversation-link strong {
            color: #7b1fa2;
          }
          
          .file-attachment {
            background: #fff;
            border: 1px solid #ddd;
            border-radius: 4px;
            padding: 8px;
            margin: 5px 0;
            font-size: 12px;
            color: #666;
          }
          
          .reactions {
            margin-top: 8px;
            font-size: 12px;
            color: #666;
          }
          
          .reaction {
            display: inline-block;
            margin-right: 8px;
          }
          
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 12px;
            color: #666;
            text-align: center;
          }
          
          @media print {
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        ${printContent}
      </body>
    </html>
  `);

  printWindow.document.close();

  // Wait for content to load, then print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };
}
